import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { BaseChartModel } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { ChartDataPoint, DefaultChartData } from "@/Patient/Models/GrowthCharts/ChartData";
import { GrowthChartsCacheData } from "@/Patient/Models/GrowthCharts/GrowthChartsCacheData";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { GrowthChartGraph } from "./Charts/GrowthChartGraph";
import { LineChartGraph } from "./Charts/LineChartGraph";
import { GrowthChartModel } from "./UIModels/ChartConfigurations/GrowthChartModel";
import { VitalSignChartModel } from "./UIModels/ChartConfigurations/VitalSignChartModel";
import { GrowthChartsDataTable } from "./GrowthChartsDataTable";
import { GrowthChartsPlotSelection, PlotType } from "./GrowthChartsPlotSelection";
import { VitalSignsDataTable } from "./VitalSignsDataTable";

interface GrowthChartsMainContentItemProps {
    chart: BaseChartModel;
    cacheData: GrowthChartsCacheData;
    unitSystem: UnitSystem;
    isBornPremature: boolean;
    bornPrematureWeeks: number;
    editExistingVitalSign: (vitalSign: MedicalItemVitalSign, reason: string) => void;
}

class GrowthChartsMainContentItemState {
    public checkedPlots: PlotType[];
    public hoverDataPoint: ChartDataPoint;
}

class GrowthChartsMainContentItem extends BaseReactComponent<GrowthChartsMainContentItemProps, GrowthChartsMainContentItemState> {
    public constructor(props: GrowthChartsMainContentItemProps) {
        super(props);
        this.state = {
            checkedPlots: [],
            hoverDataPoint: null
        };
    }

    public componentDidMount(): void {
        this.checkDefaultPlots();
    }

    public componentDidUpdate(prevProps: GrowthChartsMainContentItemProps): void {
        if (prevProps.bornPrematureWeeks !== this.props.bornPrematureWeeks ||
            prevProps.isBornPremature !== this.props.isBornPremature) {
            this.checkDefaultPlots();
        }
    }

    public render(): JSX.Element {
        const chart = this.props.chart;
        if (chart instanceof GrowthChartModel)
            return this.renderGrowChart(chart);

        if (chart instanceof VitalSignChartModel)
            return this.renderVitalSignChart(chart);

        return null;
    }

    private renderGrowChart(chart: GrowthChartModel): JSX.Element {
        const curves = this.props.cacheData?.Curves[chart.curveName];
        const hightlight = chart.data.firstOrDefault(p => p.id === this.state.hoverDataPoint?.id);

        return (
            <div id={chart.id} className="main-content__item">
                <GrowthChartsDataTable
                    chart={chart}
                    shownPlots={this.state.checkedPlots}
                    hightlightRow={hightlight}
                    onVitalSignClick={medicalItemId => this.handleEditVitalSignClick(chart, medicalItemId)}
                />
                <div className="chart-full-width">
                    {this.props.chart.isPlotSelectionSupported && (
                        <GrowthChartsPlotSelection
                            chartId={chart.id}
                            checkedPlots={this.state.checkedPlots}
                            onPlotSelectionChanged={(plotType: PlotType[]) => this.mutateState(s => s.checkedPlots = plotType)}
                        />
                    )}

                    <div className="datachart chart-full-width">
                        <GrowthChartGraph
                            defaultPercentile={new DefaultChartData(
                                curves,
                                chart.ageUnit,
                                this.props.unitSystem,
                                this.props.chart.xAxis.max,
                                this.props.chart.yAxis.max
                            )}
                            chart={chart}
                            showPrimary={this.shouldShowChronologicalPlot()}
                            showSecondary={this.shouldShowCorrectedAgePlot()}
                            onDataPointHover={dataPoint => this.mutateState(s => s.hoverDataPoint = dataPoint)}
                            onDataPointClick={medicalItemId => this.handleEditVitalSignClick(chart, medicalItemId)}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderVitalSignChart(chart: VitalSignChartModel): JSX.Element {
        const hightlight = chart.primary.dataPoints.firstOrDefault(p => p.id === this.state.hoverDataPoint?.id);
        return (
            <div id={chart.id} className="main-content__item">
                <VitalSignsDataTable
                    chart={chart}
                    hightlightRow={hightlight}
                    onVitalSignClick={medicalItemId => this.handleEditVitalSignClick(chart, medicalItemId)}
                />
                <div className="chart-full-width">
                    <div className="datachart chart-full-width">
                        <LineChartGraph
                            chart={chart}
                            onDataPointHover={dataPoint => this.mutateState(s => s.hoverDataPoint = dataPoint)}
                            onDataPointClick={medicalItemId => this.handleEditVitalSignClick(chart, medicalItemId)}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private handleEditVitalSignClick(chart: BaseChartModel, medicalItemId: string): void {
        const vitalSign = chart.getVitalSignById(medicalItemId);
        const reason = this.getVitalSignReason(vitalSign);
        this.props.editExistingVitalSign(vitalSign, reason);
    }

    private shouldShowChronologicalPlot(): boolean {
        return !this.state.checkedPlots.any()
            || this.state.checkedPlots.contains("chronological")
            || !this.props.chart.isPlotSelectionSupported;
    }

    private shouldShowCorrectedAgePlot(): boolean {
        return this.props.chart.hasBornPrematureData() && this.props.chart.isPlotSelectionSupported &&
            (!this.state.checkedPlots.any() || this.state.checkedPlots.contains("corrected"));
    }

    private checkDefaultPlots(): void {
        const checkedPlots = [];
        if (!this.props.isBornPremature || !this.props.isBornPremature) {
            checkedPlots.push("chronological") || !this.props.chart.isPlotSelectionSupported;
        }
        else {
            const correctedAge = this.props.chart.getCorrectedAge();
            if (correctedAge >= 0 && correctedAge <= 2)
                checkedPlots.push("corrected");
            else
                checkedPlots.push("chronological") || !this.props.chart.isPlotSelectionSupported;
        }

        this.mutateState(s => s.checkedPlots = checkedPlots);
    }

    private getVitalSignReason(vitalSign: MedicalItemVitalSign): string {
        const medicalSummary = patientStore.PatientMedicalSummaryModels?.[patientStore.getCurrentPatientContactId()];
        const encounter = medicalSummary?.get("Encounters").find(encounter => encounter.Id === vitalSign.RelatedEncounterId);
        return encounter?.VisitPurpose;
    }
}

export { GrowthChartsMainContentItem };
